import { useEffect, useState, type ReactNode, type RefObject } from "react";
import { flushSync } from "react-dom";
import { ChatIcon } from "../ui/icons/Icon";
import { AuthLayout } from "./AuthLayout";

// Piezas del kit para las pantallas de acceso (ingreso, segundo factor, registro, recuperar, cambiar contraseña,
// invitación y soporte): las mismas `tm-*` de Bandejas, para que todo el flujo se vea igual (V15).

/** Textos de las piezas comunes: cada pantalla con contraseña los pide junto con los suyos. */
export const authKitTextKeys = ["LOGIN_SHOW_PASSWORD", "LOGIN_HIDE_PASSWORD"];

/**
 * Marco de la pantalla: columna de 400 px centrada bajo el logo, con su título (C03). Con `back`, el atrás del kit
 * (arrow-left en botón redondo, en la misma fila que el título).
 */
export function AuthPage({
  title,
  back,
  children,
}: {
  title: string;
  back?: { href: string; label: string };
  children: ReactNode;
}) {
  return (
    <AuthLayout>
      <div className="tm auth-page">
        {back ? (
          <header className="tm-head">
            <div className="tm-head-title">
              <a className="tm-back" href={back.href} aria-label={back.label} title={back.label} data-part="atras">
                <ChatIcon name="arrow-left" />
              </a>
              <h1>{title}</h1>
            </div>
          </header>
        ) : (
          <h1>{title}</h1>
        )}
        {children}
      </div>
    </AuthLayout>
  );
}

/**
 * Aviso en línea (C14): error (rechazo del servidor, con role=alert), aviso (estado que cambia lo que se puede hacer) o
 * listo (resultado de la tarea). Quien lo usa le pone `key` por intento para que un rechazo repetido se anuncie otra vez.
 */
export function AuthNotice({ tone, children }: { tone: "error" | "warning" | "done"; children: ReactNode }) {
  const kind = tone === "error" ? " error" : tone === "warning" ? " ochre" : "";
  return (
    <div className={`tm-notice${kind}`} role={tone === "error" ? "alert" : "status"} data-part="aviso">
      <ChatIcon name={tone === "done" ? "check" : "circle-alert"} />
      <span>{children}</span>
    </div>
  );
}

/** La ayuda se calla cuando el error dice lo mismo (la regla de la contraseña): nada se dice dos veces (M6). */
const showsHelp = (error?: string | null, help?: ReactNode) => !!help && help !== error;

/** Ids que describen un campo: su error (si hay) y su ayuda (si se muestra), en ese orden. */
export function describedBy(id: string, error?: string | null, help?: ReactNode): string | undefined {
  return (
    [error ? `${id}-error` : null, showsHelp(error, help) ? `${id}-help` : null].filter(Boolean).join(" ") || undefined
  );
}

/** Campo del kit (C05): etiqueta arriba, el control, el error justo debajo y después la ayuda. */
export function AuthField({
  id,
  label,
  error,
  help,
  children,
}: {
  id: string;
  label: string;
  error?: string | null;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`tm-field${error ? " invalid" : ""}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {error ? (
        <span id={`${id}-error`} className="tm-field-error">
          {error}
        </span>
      ) : null}
      {showsHelp(error, help) ? <small id={`${id}-help`}>{help}</small> : null}
    </div>
  );
}

/**
 * Contraseña con el ojo dentro del campo: su nombre dice lo que hará («Mostrar/Ocultar contraseña»), sin aria-pressed.
 * Al enviar el formulario vuelve a ocultarse, para que el gestor de contraseñas reconozca lo que guarda.
 */
export function PasswordInput({
  id,
  name,
  autoComplete,
  inputRef,
  error,
  help,
  required,
  L,
}: {
  id: string;
  name: string;
  autoComplete: "current-password" | "new-password";
  inputRef: RefObject<HTMLInputElement | null>;
  error?: string | null;
  help?: ReactNode;
  required?: boolean;
  L: (key: string) => string;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const form = inputRef.current?.form;
    const hide = () => setVisible(false);
    form?.addEventListener("submit", hide);
    return () => form?.removeEventListener("submit", hide);
  }, [inputRef]);
  const label = L(visible ? "LOGIN_HIDE_PASSWORD" : "LOGIN_SHOW_PASSWORD");
  return (
    <span className="tm-secret">
      <input
        id={id}
        name={name}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        autoCapitalize="off"
        spellCheck={false}
        required={required}
        ref={inputRef}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy(id, error, help)}
      />
      <button
        type="button"
        className={`tm-icon-btn tm-secret-toggle${visible ? " on" : ""}`}
        aria-controls={id}
        aria-label={label}
        title={label}
        onClick={() => setVisible((value) => !value)}
      >
        <ChatIcon name="eye" />
      </button>
    </span>
  );
}

/**
 * Fila de acciones (C04/C21): secundaria y principal abajo a la derecha; en el celular, en la barra fija de abajo.
 * Cargando: la regla común de aria-busy (color-controls.css) con el mismo texto y ancho.
 */
export function AuthActions({ secondary, children }: { secondary?: ReactNode; children: ReactNode }) {
  return (
    <div className="tm-form-actions auth-actions" data-part="acciones">
      {secondary}
      {children}
    </div>
  );
}

export function SubmitButton({ sending, children }: { sending: boolean; children: ReactNode }) {
  return (
    <button type="submit" className="tm-btn primary" aria-busy={sending ? "true" : undefined}>
      {children}
    </button>
  );
}

/**
 * Dibuja ya el resultado de un envío (flushSync) y lleva el foco al primer campo con error en el orden de la pantalla
 * (el servidor puede mandarlos en otro), que en ese momento ya tiene su aria-invalid y su mensaje (C05). Sin errores de
 * campo, el foco va al título y la pantalla sube para leer el aviso: en el celular se desplaza dentro de main.
 */
export function showResult(apply: () => void, hasFieldErrors: boolean) {
  flushSync(apply);
  const field = hasFieldErrors ? document.querySelector<HTMLElement>(".auth-page [aria-invalid='true']") : null;
  if (field) {
    field.focus();
    return;
  }
  const heading = document.querySelector<HTMLElement>("h1");
  if (heading) {
    if (!heading.hasAttribute("tabindex")) heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  } else (document.activeElement as HTMLElement | null)?.blur();
  document.getElementById("main")?.scrollTo?.({ top: 0 });
  window.scrollTo({ top: 0 });
}
